import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ContainerEventMap,
  __CONTAINER_INTERNALS_DO_NOT_USE__
} from '@inno-tools/container-bridge'
import { useTranslation } from 'react-i18next'
import { CustomEvents } from 'shared/constants/customEvents'

import { ChangePasswordLayer } from '../components/ChangePasswordLayer/ChangePasswordLayer'
import { EditProfileLayer } from '../components/EditProfileLayer'
import { HomeLayer } from '../components/HomeLayer'

interface UseUserProfileLayersProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  includeBodyPaddings?: boolean
  editPreferencesButtonRef?: React.Ref<HTMLButtonElement>
}

interface Layer {
  bodyPaddings?: boolean
  title?: string
  body: React.ReactNode
  onBackClick?: () => void
}

interface UseUserProfileLayersReturn {
  layers: Layer[]
  layerIndex: number
  setLayerIndex: (index: number) => void
  openProductTour: () => void
}

export const useUserProfileLayers = ({
  isOpen,
  setIsOpen,
  editPreferencesButtonRef,
  includeBodyPaddings = false
}: UseUserProfileLayersProps): UseUserProfileLayersReturn => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'user_profile'
  })

  const [layerIndex, setLayerIndex] = useState(0)

  useEffect(() => {
    if (!isOpen) {
      setLayerIndex(0)
    }
  }, [isOpen])

  const profileModalListeners = useMemo(
    () => ({
      OPEN_PROFILE_MODAL(
        data?: ContainerEventMap['OPEN_PROFILE_MODAL']['data']
      ) {
        setIsOpen(true)

        if (data?.editProfile) setLayerIndex(1)
      }
    }),
    [setIsOpen]
  )

  __CONTAINER_INTERNALS_DO_NOT_USE__.useContainerEventListener__INTERNAL__(
    profileModalListeners
  )

  const openProductTour = useCallback(() => {
    const event = new Event(CustomEvents.OPEN_PRODUCT_TOUR)
    document.dispatchEvent(event)
    setIsOpen(false)
  }, [setIsOpen])

  const layers = useMemo(
    () =>
      [
        {
          ...(!includeBodyPaddings ? { bodyPaddings: false } : {}),
          body: (
            <HomeLayer
              editPreferencesButtonRef={editPreferencesButtonRef}
              onEditProfileClick={() => setLayerIndex(1)}
              onProductTourClick={openProductTour}
              onMenuItemClick={() => setIsOpen(false)}
            />
          ),
          onBackClick: () => setIsOpen(false)
        },
        {
          title: t('edit_profile'),
          body: (
            <EditProfileLayer
              onNavigateToChangePassword={() => setLayerIndex(2)}
            />
          ),
          onBackClick: () => setLayerIndex(0)
        },
        {
          title: t('change_password'),
          body: <ChangePasswordLayer onClose={() => setLayerIndex(1)} />,
          onBackClick: () => setLayerIndex(1)
        }
      ] satisfies Layer[],
    [
      editPreferencesButtonRef,
      includeBodyPaddings,
      openProductTour,
      t,
      setIsOpen,
      setLayerIndex
    ]
  )

  return { layers, layerIndex, setLayerIndex, openProductTour }
}
